/**
 * Who has an account, and what each of them may do — at a glance, before you
 * open anyone.
 */
import { useState } from "react";
import { allows, type Area, type Grants } from "@dp/shared";
import { api } from "../../lib/api.ts";
import { Link } from "../../lib/router.tsx";
import { useAsync } from "../../lib/useAsync.ts";
import { describeAccess, useAuth } from "../../lib/auth.tsx";
import { Loading, PageHead, Problem, Tiles } from "../../components/Page.tsx";
import { NewAccountForm } from "./NewAccountForm.tsx";

export interface AccountRow {
  id: string;
  email: string;
  name: string;
  isAdmin: boolean;
  active: boolean;
  createdAt: string;
  lastSignedInAt: string | null;
  activeSessions: number;
  grants: Grants;
}

export function Accounts() {
  const { can, user } = useAuth();
  const accounts = useAsync(() => api.get<{ accounts: AccountRow[] }>("/api/accounts"), []);
  const [adding, setAdding] = useState(false);

  const rows = accounts.data?.accounts ?? [];
  const mayEdit = can("accounts", "edit");

  return (
    <div className="stack">
      <PageHead
        title="Accounts"
        sub="There is no self-signup: an account is a grant, and it arrives with its permissions already set."
        actions={mayEdit && !adding && (
          <button className="btn btn-primary" type="button" onClick={() => setAdding(true)}>New account</button>
        )}
      />

      <Tiles
        tiles={[
          { label: "Accounts", value: rows.length, tone: "accent" },
          { label: "Administrators", value: rows.filter((row) => row.isAdmin).length, tone: "warn" },
          { label: "Signed in now", value: rows.filter((row) => row.activeSessions > 0).length, tone: "ok" },
          { label: "Deactivated", value: rows.filter((row) => !row.active).length, tone: "muted" },
        ]}
      />

      {adding && (
        <NewAccountForm
          onDone={() => { setAdding(false); accounts.reload(); }}
          onCancel={() => setAdding(false)}
        />
      )}

      <Problem error={accounts.error} />

      <div className="card">
        {accounts.loading && !accounts.data ? (
          <Loading what="accounts" />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>Person</th><th>Access</th><th>Can do</th><th>Status</th></tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      {mayEdit ? <Link to={`/accounts/${row.id}`}>{row.name}</Link> : row.name}
                      <div className="small muted">{row.email}</div>
                      {row.id === user?.id && <span className="chip" style={{ marginTop: 4 }}>you</span>}
                    </td>
                    <td>
                      {row.isAdmin
                        ? <span className="chip chip-warn">Administrator</span>
                        : <span className="chip">{describeAccess(row)}</span>}
                    </td>
                    <td><GrantSummary account={row} /></td>
                    <td>
                      {!row.active ? <span className="chip chip-bad">Deactivated</span>
                        : row.activeSessions > 0 ? <span className="chip chip-ok">Signed in</span>
                          : <span className="chip">Active</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const SHORT: Record<Area, string> = {
  configuration: "Library",
  products: "Products",
  applications: "Applications",
  accounts: "Accounts",
};

function GrantSummary({ account }: { account: AccountRow }) {
  if (account.isAdmin) return <span className="small muted">Everything</span>;

  const parts = (Object.keys(SHORT) as Area[])
    .filter((area) => allows(account, area, "view"))
    .map((area) => ({
      area,
      level: allows(account, area, "delete") ? "full" : allows(account, area, "edit") ? "edit" : "read",
    }));

  if (parts.length === 0) return <span className="small muted">Nothing yet</span>;

  return (
    <div className="chips">
      {parts.map((part) => (
        <span key={part.area} className={`chip${part.level === "read" ? "" : " chip-accent"}`}>
          {SHORT[part.area]}
          <span className="muted" style={{ fontWeight: 400 }}>
            {part.level === "full" ? " full" : part.level === "edit" ? " edit" : " read"}
          </span>
        </span>
      ))}
    </div>
  );
}
